// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {resolveWorkerAssetUrl} from '@app/features/platform/utils/WorkerAssetUrl';
import {
	SPEECH_GATE_PROCESSOR_NAME,
	SPEECH_GATE_WORKLET_SOURCE,
} from '@app/features/voice/utils/speech_vad/SpeechGateWorkletSource';
import sileroWeightsUrl from '@app/features/voice/utils/speech_vad/models/silero_vad_16k.bin';

const logger = new Logger('SpeechGate');
const WEIGHTS_TIMEOUT_MS = 8_000;
/** Silero VAD v5 16 kHz weights: 309,633 float32 values. */
const WEIGHTS_BYTES = 309_633 * 4;

let weightsPromise: Promise<ArrayBuffer> | null = null;
let disabledAfterFailure = false;
let workletModuleUrl: string | null = null;
const loadedContexts = new WeakMap<BaseAudioContext, Promise<void>>();

/** Downloads (once) the VAD weights. Safe to call early; used by the voice asset preload. */
function loadSpeechVadWeights(): Promise<ArrayBuffer> {
	if (weightsPromise) return weightsPromise;
	const controller = new AbortController();
	const timer = window.setTimeout(() => controller.abort(), WEIGHTS_TIMEOUT_MS);
	weightsPromise = fetch(new URL(resolveWorkerAssetUrl(sileroWeightsUrl), window.location.href).href, {
		credentials: 'omit',
		signal: controller.signal,
	})
		.then(async (response) => {
			if (!response.ok) throw new Error(`VAD weights request failed: ${response.status}`);
			const buffer = await response.arrayBuffer();
			if (buffer.byteLength !== WEIGHTS_BYTES) throw new Error('VAD weights have an unexpected size');
			return buffer;
		})
		.finally(() => window.clearTimeout(timer));
	weightsPromise.catch(() => {
		weightsPromise = null;
	});
	return weightsPromise;
}

/** Background warm-up before joining voice. */
export function preloadSpeechVad(): Promise<void> {
	if (disabledAfterFailure) return Promise.resolve();
	return loadSpeechVadWeights().then(
		() => undefined,
		(error: unknown) => {
			logger.debug('Speech VAD preload skipped', {error});
		},
	);
}

export function isSpeechGateAvailable(): boolean {
	return !disabledAfterFailure && typeof AudioWorkletNode !== 'undefined';
}

function loadWorkletModule(context: BaseAudioContext): Promise<void> {
	let loading = loadedContexts.get(context);
	if (!loading) {
		workletModuleUrl ??= URL.createObjectURL(new Blob([SPEECH_GATE_WORKLET_SOURCE], {type: 'application/javascript'}));
		loading = context.audioWorklet.addModule(workletModuleUrl).catch((error: unknown) => {
			loadedContexts.delete(context);
			throw error;
		});
		loadedContexts.set(context, loading);
	}
	return loading;
}

export interface SpeechGateNode {
	readonly node: AudioWorkletNode;
	dispose(): void;
}

export async function createSpeechGateNode(context: AudioContext): Promise<SpeechGateNode> {
	if (!isSpeechGateAvailable()) throw new Error('Speech gate unavailable');
	let weights: ArrayBuffer;
	try {
		[weights] = await Promise.all([loadSpeechVadWeights(), loadWorkletModule(context)]);
	} catch (error) {
		disabledAfterFailure = true;
		throw error;
	}
	const node = new AudioWorkletNode(context, SPEECH_GATE_PROCESSOR_NAME, {
		numberOfInputs: 1,
		numberOfOutputs: 1,
		outputChannelCount: [1],
		channelCount: 1,
		channelCountMode: 'explicit',
		channelInterpretation: 'speakers',
	});
	// Each node owns its copy: the model keeps per-stream LSTM state on the audio thread.
	const copy = weights.slice(0);
	node.port.postMessage({type: 'weights', buffer: copy}, [copy]);
	let disposed = false;
	return {
		node,
		dispose: () => {
			if (disposed) return;
			disposed = true;
			try {
				node.port.postMessage({type: 'dispose'});
				node.disconnect();
			} catch {
				// Node may already be gone with its context.
			}
		},
	};
}
